/**
 * Exit Intent Popup Component
 * Shows when customer is about to leave - offers discount + competitor comparison
 * AI-powered retention with max 18% below competitor pricing
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { 
  X, AlertTriangle, Clock, Star, MapPin, Zap, 
  Check, ArrowRight, TrendingDown, Shield, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExitIntentPopup({ 
  serviceType, 
  originalPrice, 
  onAccept, 
  onDecline,
  pageUrl = window.location.href 
}) {
  const { user, getAuthHeaders } = useAuth();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retentionData, setRetentionData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes
  const [sessionId] = useState(() => `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [pageTime, setPageTime] = useState(0);

  // Track time on page
  useEffect(() => {
    const interval = setInterval(() => {
      setPageTime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer for offer
  useEffect(() => {
    if (show && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [show, timeLeft]);

  // Detect exit intent
  const handleMouseLeave = useCallback((e) => {
    // Only trigger when mouse leaves toward the top of the page
    if (e.clientY < 50 && !show && pageTime > 5) {
      triggerExitIntent();
    }
  }, [show, pageTime]);

  // Handle back button / page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !show && pageTime > 10) {
        // User switched tabs or minimized
        triggerExitIntent();
      }
    };

    const handleBeforeUnload = (e) => {
      if (!show && pageTime > 5) {
        triggerExitIntent();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleMouseLeave, show, pageTime]);

  const triggerExitIntent = async () => {
    if (show || !serviceType || !originalPrice) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/retention/exit-intent`,
        {
          user_id: user?.user_id || null,
          session_id: sessionId,
          service_type: serviceType,
          original_price: originalPrice,
          page_url: pageUrl,
          time_on_page: pageTime,
          scroll_depth: Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100) || 0
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setRetentionData(response.data);
      setShow(true);
    } catch (error) {
      console.error('Exit intent error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/retention/exit-intent/accept`,
        null,
        { 
          params: { 
            session_id: sessionId, 
            offer_code: retentionData?.retention_offer?.offer_code 
          },
          headers: getAuthHeaders(),
          withCredentials: true 
        }
      );
      
      if (onAccept) {
        onAccept({
          newPrice: retentionData.retention_offer.new_price,
          offerCode: retentionData.retention_offer.offer_code,
          discountPercent: retentionData.retention_offer.discount_percent
        });
      }
      setShow(false);
    } catch (error) {
      console.error('Accept offer error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    setShow(false);
    if (onDecline) onDecline();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!show || !retentionData) return null;

  const { retention_offer, competitor_comparison, jiffys_advantages } = retentionData;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="exit-intent-popup"
    >
      <Card className="w-full max-w-lg animate-in zoom-in-95 duration-300">
        {/* Header with urgency */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-4 text-white relative overflow-hidden">
          <button 
            onClick={handleDecline}
            className="absolute top-3 right-3 text-white/80 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-bold">WAIT! Don't Overpay!</span>
          </div>
          
          <p className="text-sm text-white/90">
            We noticed you're about to leave. Here's a special offer just for you!
          </p>
          
          {/* Countdown */}
          <div className="mt-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Offer expires in: </span>
            <span className="font-mono font-bold text-lg">{formatTime(timeLeft)}</span>
          </div>
        </div>

        <CardContent className="p-6 space-y-5">
          {/* Competitor Comparison */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-500" />
              Why Pay More?
            </h4>
            
            <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
              <div>
                <p className="font-medium text-red-800">{competitor_comparison.highest_priced.name}</p>
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <Star className="w-3 h-3" />
                  <span>{competitor_comparison.highest_priced.rating} rating</span>
                  <span>•</span>
                  <span>{competitor_comparison.highest_priced.response_time}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-red-600 line-through">
                  ${competitor_comparison.highest_priced.price}
                </p>
                <p className="text-xs text-red-500">Competitor price</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600">
              {competitor_comparison.message}
            </p>
          </div>

          {/* Your Discounted Price */}
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800">Your Special Price</span>
              </div>
              <Badge className="bg-green-600">
                {retention_offer.discount_percent}% OFF
              </Badge>
            </div>
            
            <div className="flex items-center justify-center gap-4 py-3">
              <div className="text-center">
                <p className="text-sm text-gray-500">Was</p>
                <p className="text-xl text-gray-400 line-through">${retention_offer.original_price}</p>
              </div>
              <ArrowRight className="w-6 h-6 text-green-500" />
              <div className="text-center">
                <p className="text-sm text-green-600 font-medium">Now</p>
                <p className="text-4xl font-bold text-green-600">${retention_offer.new_price}</p>
              </div>
            </div>
            
            <div className="text-center mt-2">
              <p className="text-sm text-green-700">
                You save <span className="font-bold">${retention_offer.discount_amount}</span> 
                {competitor_comparison.your_savings > 0 && (
                  <span> (${competitor_comparison.your_savings} less than {competitor_comparison.highest_priced.name}!)</span>
                )}
              </p>
            </div>
          </div>

          {/* Jiffys Advantages */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Why Choose Jiffys?</h4>
            <ul className="space-y-2">
              {jiffys_advantages.map((advantage, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>{advantage}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            <Button
              onClick={handleAccept}
              disabled={loading || timeLeft <= 0}
              className="w-full h-14 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-lg font-bold"
              data-testid="accept-discount-btn"
            >
              {loading ? (
                <span className="animate-pulse">Processing...</span>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Accept ${retention_offer.new_price} & Continue
                </>
              )}
            </Button>
            
            <button
              onClick={handleDecline}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-2"
            >
              No thanks, I'll pay full price elsewhere
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 pt-2 border-t text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Price Guaranteed
            </span>
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3" />
              4.8★ Rated
            </span>
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Fast Response
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Hook to use exit intent tracking
export function useExitIntent(serviceType, price) {
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [offerCode, setOfferCode] = useState(null);

  const handleAccept = (data) => {
    setDiscountedPrice(data.newPrice);
    setOfferCode(data.offerCode);
  };

  return {
    ExitIntentComponent: (
      <ExitIntentPopup
        serviceType={serviceType}
        originalPrice={price}
        onAccept={handleAccept}
      />
    ),
    discountedPrice,
    offerCode,
    hasDiscount: !!discountedPrice
  };
}
